import React, { useMemo, useState } from "react";
import type { SiteId } from "@siteagain/shared-types";

import type { Locale } from "../../i18n";
import type { OperatorCopy } from "../copy";
import { formatMonth, humanize, relativeTime } from "../format";
import { SiteMark } from "../SiteMark";
import { CHIP, inboxSubtitle, setupFacts, siteStage, type InboxItem, type InboxKind } from "./data";
import type { MC2Copy } from "./copy";
import { TopSearch } from "./shell";

type Filter = "all" | InboxKind;

export function InboxPage({
  items,
  selectedId,
  onSelect,
  sites,
  copy,
  ops,
  locale,
  now,
  busyKey,
  onOpenSite,
  onOpenAudit,
  onOpenReport,
  onRetry,
  onDecide,
  onRunAudit,
  onTakeIncident,
  onResolveIncident,
  incidentBusyId,
}: {
  items: InboxItem[];
  selectedId: string | null;
  onSelect(id: string): void;
  sites: readonly import("@siteagain/shared-types").OperatorSiteSnapshot[];
  copy: MC2Copy;
  ops: OperatorCopy;
  locale: Locale;
  now: number;
  busyKey: string;
  onOpenSite(siteId: SiteId): void;
  onOpenAudit(id: import("@siteagain/shared-types").SeoAuditRunId): void;
  onOpenReport(id: import("@siteagain/shared-types").MonthlyReportId): void;
  onRetry(item: Extract<InboxItem, { kind: "stopped" }>): void;
  onDecide(item: Extract<InboxItem, { kind: "approve" }>, action: "approve" | "reject"): void;
  onRunAudit(siteId: SiteId): void;
  onTakeIncident(incident: Extract<InboxItem, { kind: "incident" }>["incident"]): void;
  onResolveIncident(incident: Extract<InboxItem, { kind: "incident" }>["incident"]): void;
  incidentBusyId: string;
}) {
  const t = copy.inbox;
  const [filter, setFilter] = useState<Filter>("all");
  const [query, setQuery] = useState("");

  const counts = useMemo(() => {
    const map = new Map<Filter, number>([["all", items.length]]);
    for (const item of items) map.set(item.kind, (map.get(item.kind) ?? 0) + 1);
    return map;
  }, [items]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return items.filter((item) => {
      if (filter !== "all" && item.kind !== filter) return false;
      if (!needle) return true;
      return [item.title, item.subtitle, item.siteName].some((part) => part.toLowerCase().includes(needle));
    });
  }, [filter, items, query]);

  const selected = items.find((item) => item.id === selectedId) ?? visible[0] ?? null;
  const filters: Array<{ id: Filter; label: string }> = [
    { id: "all", label: t.all },
    { id: "stopped", label: t.alerts },
    { id: "setup", label: t.setup },
    { id: "approve", label: t.previews },
    { id: "audit", label: t.audits },
    { id: "report", label: t.updates },
    { id: "incident", label: t.incidents },
  ];

  return (
    <>
      <div className="mc2-topbar">
        <div>
          <div className="mc2-eyebrow">{t.eyebrow}</div>
          <h1 className="mc2-serif" style={{ fontSize: 40, lineHeight: 1.05, margin: "6px 0 0" }}>{t.title(items.length)}</h1>
        </div>
        <TopSearch sites={sites} placeholder={t.filter} ariaLabel={t.filter} onOpenSite={onOpenSite} value={query} onChange={setQuery} />
      </div>

      <div className="mc2-inbox">
        <div className="mc2-filters" role="group" aria-label={t.eyebrow}>
          {filters.map((entry) => {
            const count = counts.get(entry.id) ?? 0;
            if (count === 0 && entry.id !== "all") return null;
            return (
              <button key={entry.id} type="button" className={`mc2-filt${filter === entry.id ? " on" : ""}`} onClick={() => setFilter(entry.id)}>
                <span>{entry.label}</span><span className="n">{count}</span>
              </button>
            );
          })}
        </div>

        <section className="mc2-card mc2-inbox-list" aria-label={t.eyebrow}>
          {visible.length === 0 ? <p className="mc2-empty">{t.empty}</p> : visible.map((item) => {
            const meta = kindMeta(item.kind, copy);
            return (
              <button key={item.id} type="button" className={`mc2-irow${selected?.id === item.id ? " on" : ""}`} aria-pressed={selected?.id === item.id} onClick={() => onSelect(item.id)}>
                <span className="rowtop">
                  <span className={CHIP[meta.tone]}>{meta.label}</span>
                  <span className="mc2-mono mc2-muted" style={{ fontSize: 12 }}>{relativeTime(item.at, locale, now)}</span>
                </span>
                <span className="title">{item.kind === "report" ? `${formatMonth(item.report.month, locale)} update ready` : item.title}</span>
                <span className="sub">{inboxSubtitle(item, copy, ops)}</span>
              </button>
            );
          })}
        </section>

        <section className="mc2-card mc2-detail" aria-label={t.select}>
          {!selected ? (
            <p className="mc2-empty">{t.select}</p>
          ) : (
            <InboxDetail
              item={selected}
              sites={sites}
              copy={copy}
              ops={ops}
              locale={locale}
              now={now}
              busyKey={busyKey}
              onOpenSite={onOpenSite}
              onOpenAudit={onOpenAudit}
              onOpenReport={onOpenReport}
              onRetry={onRetry}
              onDecide={onDecide}
              onRunAudit={onRunAudit}
              onTakeIncident={onTakeIncident}
              onResolveIncident={onResolveIncident}
              incidentBusyId={incidentBusyId}
            />
          )}
        </section>
      </div>
    </>
  );
}

function InboxDetail({
  item,
  sites,
  copy,
  ops,
  locale,
  now,
  busyKey,
  onOpenSite,
  onOpenAudit,
  onOpenReport,
  onRetry,
  onDecide,
  onRunAudit,
  onTakeIncident,
  onResolveIncident,
  incidentBusyId,
}: {
  item: InboxItem;
  sites: readonly import("@siteagain/shared-types").OperatorSiteSnapshot[];
  copy: MC2Copy;
  ops: OperatorCopy;
  locale: Locale;
  now: number;
  busyKey: string;
  onOpenSite(siteId: SiteId): void;
  onOpenAudit(id: import("@siteagain/shared-types").SeoAuditRunId): void;
  onOpenReport(id: import("@siteagain/shared-types").MonthlyReportId): void;
  onRetry(item: Extract<InboxItem, { kind: "stopped" }>): void;
  onDecide(item: Extract<InboxItem, { kind: "approve" }>, action: "approve" | "reject"): void;
  onRunAudit(siteId: SiteId): void;
  onTakeIncident(incident: Extract<InboxItem, { kind: "incident" }>["incident"]): void;
  onResolveIncident(incident: Extract<InboxItem, { kind: "incident" }>["incident"]): void;
  incidentBusyId: string;
}) {
  const t = copy.inbox;
  const site = sites.find((entry) => entry.id === item.siteId) ?? null;
  const siteLink = (
    <button type="button" className="mc2-linkbtn" style={{ border: 0, background: "transparent", cursor: "pointer", padding: 0, display: "inline-flex", alignItems: "center", gap: 8 }} onClick={() => onOpenSite(item.siteId)}>
      <SiteMark siteId={item.siteId} name={item.siteName} />{item.siteName}
    </button>
  );

  if (item.kind === "stopped") {
    const busy = busyKey.startsWith(`${item.card.key}:`);
    return (
      <>
        <div className="mc2-sec">
          <div className={CHIP.down}>{t.kind.stopped}</div>
          <h2>{item.title}</h2>
          <div className="mc2-muted">{item.siteName} · {relativeTime(item.at, locale, now)}</div>
        </div>
        <div className="mc2-sec">
          <div className="mc2-eyebrow" style={{ marginBottom: 6 }}>{t.timeline}</div>
          <p style={{ margin: 0, fontSize: 15 }}>{item.card.errorMessage ?? ops.pipeline.noErrorMessage}</p>
        </div>
        <div className="mc2-sec soft">
          <div className="mc2-actions">
            {item.card.recommendationId && (
              <button type="button" className="mc2-btn" disabled={busy} onClick={() => onRetry(item)}>{busy ? ops.action.working : t.retry}</button>
            )}
            <button type="button" className="mc2-btn ghost" onClick={() => onOpenSite(item.siteId)}>{t.openSite}</button>
          </div>
        </div>
      </>
    );
  }

  if (item.kind === "approve") {
    const busy = busyKey.startsWith(`${item.card.key}:`);
    return (
      <>
        <div className="mc2-sec">
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span className={CHIP.ink}>{t.kind.approve}</span>
            {item.card.safetyClass && <span className={CHIP.neutral}>{ops.pipeline.safety[item.card.safetyClass]}</span>}
            {item.card.batch?.eligible && <span className={CHIP.up}>{ops.pipeline.allChecksPassed}</span>}
          </div>
          <h2>{item.title}</h2>
          <div className="mc2-muted">{siteLink} · {relativeTime(item.at, locale, now)}</div>
        </div>
        <div className="mc2-sec">
          <div className="mc2-eyebrow" style={{ marginBottom: 6 }}>{t.expected}</div>
          <p style={{ margin: 0, fontSize: 15 }}>{t.noExpected}</p>
        </div>
        {item.card.previewUrl && (
          <div className="mc2-sec">
            <a className="mc2-linkbtn" href={item.card.previewUrl} target="_blank" rel="noreferrer">{ops.action.openPreview} ↗</a>
          </div>
        )}
        <div className="mc2-sec soft" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button type="button" className="mc2-btn" disabled={busy} onClick={() => onDecide(item, "approve")}>{busy ? ops.action.working : ops.action.approve}</button>
          <button type="button" className="mc2-btn ghost" disabled={busy} onClick={() => onDecide(item, "reject")}>{ops.action.reject}</button>
          <button type="button" className="mc2-btn ghost" onClick={() => onOpenSite(item.siteId)}>{t.openSite}</button>
        </div>
      </>
    );
  }

  if (item.kind === "audit") {
    return (
      <>
        <div className="mc2-sec">
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span className={CHIP.info}>{t.kind.audit}</span>
            {site?.latestSeoScore !== null && site?.latestSeoScore !== undefined && <span className={CHIP.neutral}>{Math.round(site.latestSeoScore)} / 100</span>}
          </div>
          <h2>{item.title}</h2>
          <div className="mc2-muted">{siteLink} · {relativeTime(item.at, locale, now)}</div>
        </div>
        <div className="mc2-sec">
          <p style={{ margin: 0, fontSize: 15 }}>{item.subtitle}</p>
        </div>
        <div className="mc2-sec soft" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button type="button" className="mc2-btn" onClick={() => onOpenAudit(item.auditId)}>{t.openReport}</button>
          <button type="button" className="mc2-btn ghost" onClick={() => onRunAudit(item.siteId)}>{ops.action.runAudit}</button>
          <button type="button" className="mc2-btn ghost" onClick={() => onOpenSite(item.siteId)}>{t.openSite}</button>
        </div>
      </>
    );
  }

  if (item.kind === "report") {
    return (
      <>
        <div className="mc2-sec">
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span className={CHIP.lime}>{t.kind.report}</span>
            <span className={CHIP.neutral}>{formatMonth(item.report.month, locale)}</span>
          </div>
          <h2>{item.title}</h2>
          <div className="mc2-muted">{siteLink} · {relativeTime(item.at, locale, now)}</div>
        </div>
        <div className="mc2-sec">
          <div className="mc2-eyebrow" style={{ marginBottom: 8 }}>{ops.reports.title}</div>
          <p style={{ margin: 0, fontSize: 15 }}>
            {item.report.totals.shipped} {ops.reports.columns.shipped.toLowerCase()} · {item.report.totals.rolledBack} {ops.reports.columns.rolledBack.toLowerCase()} · {item.report.totals.measured} {ops.reports.columns.measured.toLowerCase()}.
          </p>
        </div>
        <div className="mc2-sec soft" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button type="button" className="mc2-btn" onClick={() => onOpenReport(item.report.id)}>{t.review}</button>
          <button type="button" className="mc2-btn ghost" onClick={() => onOpenSite(item.siteId)}>{t.openSite}</button>
        </div>
      </>
    );
  }

  if (item.kind === "setup") {
    const facts = setupFacts(item.site);
    const stage = siteStage(item.site, locale);
    const factsCopy = copy.fleet.setup;
    const factsLabel: Record<"audit" | "preview" | "live", string> = { audit: factsCopy.audit, preview: factsCopy.preview, live: factsCopy.live };
    return (
      <>
        <div className="mc2-sec">
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span className={CHIP.warn}>{t.kind.setup}</span>
            <span className={CHIP.neutral}>{stage.label}</span>
          </div>
          <h2>{item.title}</h2>
          <div className="mc2-muted">{siteLink} · {relativeTime(item.at, locale, now)}</div>
        </div>
        <div className="mc2-sec">
          <div className="mc2-eyebrow" style={{ marginBottom: 6 }}>{t.checklist}</div>
          {facts.map((fact) => (
            <div className="mc2-step" key={fact.key}>
              <span className={`mc2-sdot ${fact.ok ? "done" : "now"}`}>{fact.ok ? "✓" : "!"}</span>
              <div>
                <div style={{ fontWeight: 500 }}>{factsLabel[fact.key]}</div>
                <div className="mc2-muted" style={{ fontSize: 13 }}>{fact.ok ? copy.fleet.setupOk : copy.fleet.setupMissing}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="mc2-sec">
          <div className="mc2-eyebrow" style={{ marginBottom: 8 }}>{t.waitingOn}</div>
          <div style={{ padding: "6px 0", fontSize: 14 }}>· {copy.inbox.changesWaiting(item.site.awaitingApproval + item.site.pendingRecommendations)}</div>
          {item.site.openIncidents > 0 && <div style={{ padding: "6px 0", fontSize: 14 }}>· {copy.fleet.incidents(item.site.openIncidents)}</div>}
        </div>
        <div className="mc2-sec soft" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button type="button" className="mc2-btn" onClick={() => onOpenSite(item.siteId)}>{t.openSite}</button>
          <button type="button" className="mc2-btn ghost" onClick={() => onRunAudit(item.siteId)}>{ops.action.runAudit}</button>
        </div>
      </>
    );
  }

  const busy = incidentBusyId === item.incident.id;
  return (
    <>
      <div className="mc2-sec">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <span className={CHIP.down}>{t.kind.incident}</span>
          <span className={CHIP.neutral}>{copy.inbox.severity[item.incident.severity] ?? humanize(item.incident.severity)}</span>
          <span className={CHIP.neutral}>{copy.inbox.issue[item.incident.issueType] ?? humanize(item.incident.issueType)}</span>
        </div>
        <h2>{item.incident.summary}</h2>
        <div className="mc2-muted">{siteLink} · {relativeTime(item.at, locale, now)}</div>
      </div>
      <div className="mc2-sec">
        <div className="mc2-eyebrow" style={{ marginBottom: 6 }}>{t.timeline}</div>
        <p style={{ margin: 0, fontSize: 15 }}>
          {copy.inbox.issue[item.incident.issueType] ?? humanize(item.incident.issueType)} on {item.incident.siteName}, detected {relativeTime(item.at, locale, now)}.
          {" "}
          {item.incident.assignedOwner ? t.ownedBy(item.incident.assignedOwner) : (locale === "fr" ? "Personne ne l'a encore pris en charge." : "No one has taken ownership yet.")}
        </p>
      </div>
      <div className="mc2-sec soft" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {!item.incident.assignedOwner && (
          <button type="button" className="mc2-btn ghost" disabled={busy} onClick={() => onTakeIncident(item.incident)}>{busy ? ops.action.working : t.take}</button>
        )}
        <button type="button" className="mc2-btn" disabled={busy} onClick={() => onResolveIncident(item.incident)}>{busy ? ops.action.working : t.resolve}</button>
        <button type="button" className="mc2-btn ghost" onClick={() => onOpenSite(item.siteId)}>{t.openSite}</button>
      </div>
    </>
  );
}

function kindMeta(kind: InboxKind, copy: MC2Copy): { label: string; tone: keyof typeof CHIP } {
  switch (kind) {
    case "stopped": return { label: copy.inbox.kind.stopped, tone: "down" };
    case "approve": return { label: copy.inbox.kind.approve, tone: "ink" };
    case "audit": return { label: copy.inbox.kind.audit, tone: "info" };
    case "report": return { label: copy.inbox.kind.report, tone: "lime" };
    case "setup": return { label: copy.inbox.kind.setup, tone: "warn" };
    case "incident": return { label: copy.inbox.kind.incident, tone: "down" };
    default: return { label: kind, tone: "neutral" };
  }
}
